import { logger } from '@/utils/logger';

/**
 * Copy a file chosen from the OS picker into memory.
 *
 * Android hands back a File backed by a `content://` reference whose read
 * permission can lapse after the pick — the OS then reports
 * `NotReadableError: The requested file could not be read...`. Observed on a
 * Galaxy S22 for photos of 1.5MB, 3.7MB and 6.2MB alike, so it is a lifetime
 * problem, not a size one, and it bites hardest for a file held in React state
 * until a later submit (an onboarding certificate can sit there for minutes).
 *
 * Reading once, at pick time, touches the fragile reference a single time;
 * everything afterwards works from RAM. The read is retried once because the
 * failure is transient.
 */
export async function snapshotPickedFile(file: File): Promise<File | null> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const buffer = await file.arrayBuffer();
      return new File([buffer], file.name, { type: file.type });
    } catch (err) {
      const detail = {
        attempt,
        name: file.name,
        type: file.type || '(none)',
        sizeKB: Math.round(file.size / 1024),
        error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      };
      if (attempt === 1) {
        logger.warn('Could not read picked file, retrying', detail);
      } else {
        logger.error('Picked file could not be read', detail);
      }
    }
  }
  return null;
}
